/**
 * useSpeechDetection:
 * 
 * A hook to monitor microphone audio levels and detect when the user is speaking
 * without transmitting, as well as detecting silence when the microphone is active.
 * It provides state flags to indicate various conditions for UI feedback. Most of these
 * flags are 'instant start, slow off' to avoid flickering, using timers.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { MicrophoneImpl, WaveformCallback } from "../../utils/MicrophoneImpl";

// Constants for audio level detection
const SPEECH_THRESHOLD = 400; // Threshold for considering audio as speech
const SPEECH_DETECT_INTERVAL_MS = 200; // Check every 200ms
const TALKING_NOTIFICATION_TIMEOUT_MS = 1000; // 1 second before hiding notification
const SILENCE_TIMEOUT_MS = 4000; // 4 seconds before showing silence notification
const STILL_WITH_US_TIMEOUT_MS = 8000; // 8 seconds before showing "Still with us?"
const RESUMED_TALKING_TIMEOUT_MS = 2000; // 2 seconds to show "There you are" message

function averageAudioLevel(data: Int16Array): number {
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    sum += Math.abs(data[i]);
  }
  return sum / data.length;
}

export function useSpeechDetection(
  mic: MicrophoneImpl,
  isActive: boolean,
  hasEverTransmitted: boolean
) {
  const [isTalkingButNotTransmitting, setIsTalkingButNotTransmitting] = useState(false);
  const [isSilentWhileActive, setIsSilentWhileActive] = useState(false);
  const [isResumedTalking, setIsResumedTalking] = useState(false);
  const [isStillWithUs, setIsStillWithUs] = useState(false);
  const [hasSpokenDuringSession, setHasSpokenDuringSession] = useState(false);

  // Audio levels for speech detection
  const audioLevelRef = useRef<number>(0);
  const talkingTimerRef = useRef<number | null>(null);
  const silenceTimerRef = useRef<number | null>(null);
  const resumedTalkingTimerRef = useRef<number | null>(null);
  const stillWithUsTimerRef = useRef<number | null>(null);

  // Check if the user is talking but not transmitting (not holding spacebar).
  const checkForSpeechWhileInactive = useCallback(() => {
    if (!isActive && audioLevelRef.current > SPEECH_THRESHOLD) {
      // User is talking but microphone is not active

      // Show notification if not already shown
      setIsTalkingButNotTransmitting(true);

      // Clear existing timer if already set
      if (talkingTimerRef.current !== null) {
        window.clearTimeout(talkingTimerRef.current);
        talkingTimerRef.current = null;
      }

      // Set timer to hide notification after 1 second
      talkingTimerRef.current = window.setTimeout(() => {
        console.debug("[useSpeechDetection] talkingTimerRef expired - hiding notification");
        setIsTalkingButNotTransmitting(false);
        talkingTimerRef.current = null;
      }, TALKING_NOTIFICATION_TIMEOUT_MS);
    }
  }, [isActive, hasEverTransmitted]);

  // Setup audio level monitoring
  useEffect(() => {
    const audioDetectionCallback: WaveformCallback = (data) => {
      audioLevelRef.current = averageAudioLevel(data);
    };

    mic.addWaveformTap("audiodetector", audioDetectionCallback);

    // Start periodic check for speech detection
    const intervalId = window.setInterval(() => {
      checkForSpeechWhileInactive();
    }, SPEECH_DETECT_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      mic.removeWaveformTap("audiodetector");
    };
  }, [mic, checkForSpeechWhileInactive]);

  // Clear notification and timer when microphone becomes active
  useEffect(() => {
    if (isActive && isTalkingButNotTransmitting) {
      setIsTalkingButNotTransmitting(false);

      if (talkingTimerRef.current !== null) {
        window.clearTimeout(talkingTimerRef.current);
        talkingTimerRef.current = null;
      }
    }
  }, [isActive, isTalkingButNotTransmitting]);

  // Monitor for silence when microphone is active
  useEffect(() => {
    // Only monitor when microphone is active
    if (!isActive) {
      // Clear any existing timers and states if microphone is deactivated
      if (silenceTimerRef.current !== null) {
        window.clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      if (resumedTalkingTimerRef.current !== null) {
        window.clearTimeout(resumedTalkingTimerRef.current);
        resumedTalkingTimerRef.current = null;
      }
      if (stillWithUsTimerRef.current !== null) {
        window.clearTimeout(stillWithUsTimerRef.current);
        stillWithUsTimerRef.current = null;
      }
      setIsSilentWhileActive(false);
      setIsResumedTalking(false);
      setIsStillWithUs(false);
      setHasSpokenDuringSession(false);
      return;
    }

    // Function to check audio levels
    const checkForSilence = () => {
      if (isActive) {
        if (audioLevelRef.current > SPEECH_THRESHOLD) {
          // Audio detected - user is speaking
          setHasSpokenDuringSession(true);

          // Cancel any silence timers if audio is detected
          if (silenceTimerRef.current !== null) {
            window.clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }

          if (stillWithUsTimerRef.current !== null) {
            window.clearTimeout(stillWithUsTimerRef.current);
            stillWithUsTimerRef.current = null;
          }

          // If we were previously silent or in "still with us" state and now hearing audio again
          if ((isSilentWhileActive || isStillWithUs) && !isResumedTalking) {
            setIsSilentWhileActive(false);
            setIsStillWithUs(false);
            setIsResumedTalking(true);

            // Set timer to return to normal waveform display
            if (resumedTalkingTimerRef.current !== null) {
              window.clearTimeout(resumedTalkingTimerRef.current);
            }

            resumedTalkingTimerRef.current = window.setTimeout(() => {
              console.debug("[useSpeechDetection] Resumed talking timeout - back to waveform");
              setIsResumedTalking(false);
              resumedTalkingTimerRef.current = null;
            }, RESUMED_TALKING_TIMEOUT_MS);
          }
        } else {
          // No audio detected while active
          if (hasSpokenDuringSession) {
            // User has spoken during this session but is now silent

            // If we're not already in a silent state and no timer is running
            if (!isSilentWhileActive && !isStillWithUs && silenceTimerRef.current === null) {
              // Skip "We're not hearing anything" state and go directly to "Still with us?" after 8 seconds
              silenceTimerRef.current = window.setTimeout(() => {
                console.debug("[useSpeechDetection] Extended silence after speaking - showing 'Still with us?'");
                setIsStillWithUs(true);
                silenceTimerRef.current = null;
              }, STILL_WITH_US_TIMEOUT_MS);
            }
          } else {
            // User hasn't spoken yet during this session
            if (silenceTimerRef.current === null && !isSilentWhileActive) {
              // Start silence timer if not already running
              silenceTimerRef.current = window.setTimeout(() => {
                console.debug("[useSpeechDetection] Silence detected while microphone active - no speech yet");
                setIsSilentWhileActive(true);
                silenceTimerRef.current = null;
              }, SILENCE_TIMEOUT_MS);
            }
          }
        }
      }
    };

    // Check for silence periodically
    const intervalId = window.setInterval(checkForSilence, SPEECH_DETECT_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isActive, isSilentWhileActive, isResumedTalking, isStillWithUs]);

  return {
    isTalkingButNotTransmitting,
    isSilentWhileActive,
    isResumedTalking,
    isStillWithUs,
  };
}
